import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function DorsetCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Dorset Pothole Claims | 43,000 Repairs in Five Years | Fixtyer"
        description="Dorset Council filled 43,115 carriageway potholes in five years, B/C roads hit 13.3% RED in 2022, and 51% of the network is unclassified roads assessed only periodically. See what the DfT AMBER rating means for your claim."
        canonical="/councils/dorset"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Dorset', url: '/councils/dorset' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Dorset has GREEN best practice — does that block my pothole claim?',
            answer: 'No. Best practice measures how the council manages its assets — ISO-aligned plans, documented strategy, innovative treatments. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Dorset is AMBER on condition, with B/C roads hitting 13.31% RED in 2022 and over 10,000 carriageway potholes filled in both 2023/24 and 2024/25.',
          },
          {
            question: 'What happened to Dorset road condition in 2022?',
            answer: 'B and C roads in RED condition jumped from 5.6% in 2021 to 13.31% in 2022 — nearly quadruple the 2020 baseline of 3.41%. Unclassified roads in RED hit 25%, up from 12.8% the year before. Dorset\'s own report notes the 2022 B/C figures "reflect a more detailed breakdown due to the survey technology used (non-SCANNER)" — but the council still published the numbers and they remain on the public record.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 1,944.7km — 51% of Dorset\'s 3,797km network. The council assesses them "periodically" rather than with the annual SCANNER surveys used on classified roads. At the last survey, 12% of U-roads were in RED condition — roughly 233km of residential streets, estate roads and village routes. Only RED percentages are published for U-roads; there is no amber/green breakdown.',
          },
          {
            question: 'Dorset plans 90% preventative spend in 2025/26 — can I still claim?',
            answer: 'Yes. Preventative spend is a portfolio metric, not a defence for a specific pothole. Dorset\'s reactive share rose to 18% in 2022/23 and 2023/24 — the same years carriageway pothole fills jumped from 7,643 to over 10,600. The DfT Spend scorecard is AMBER, and in 2020/21 the council spent £16.15m against a £20.48m DfT allocation — 21% below the funded level.',
          },
          {
            question: 'Pothole repairs jumped in 2023/24 — does that mean Dorset\'s roads are fixed?',
            answer: 'No. Dorset filled 10,625 carriageway potholes in 2023/24 and 10,492 in 2024/25 — a 39% jump from 2022/23 and still running at roughly 29 repairs every day. A network producing defects at that rate is one where potholes routinely form between inspections. Prior reports and photographic evidence of the specific defect remain decisive.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Dorset Council"
        councilSlug="dorset"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/dorset"
        headline="Dorset Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Dorset</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Dorset: GREEN Best Practice, AMBER Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Dorset Council earns a <strong className="text-emerald-400">GREEN</strong> best-practice scorecard
            for its asset management strategy and innovative treatments — yet the overall DfT rating is{' '}
            <strong className="text-amber-400">AMBER</strong> on condition and spend. B/C roads hit{' '}
            <strong className="text-rose-400">13.31% RED in 2022</strong>, unclassified roads spiked to 25% RED
            the same year, and the council filled{' '}
            <strong className="text-rose-400">43,115 carriageway potholes in five years</strong> across its
            3,797km network.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">10,492</div>
            <div className="text-white font-medium mt-2">Carriageway potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              Up 39% from 2022/23's 7,643 — roughly 29 pothole repairs every single day across Dorset,
              even as the council targets 90% preventative spend in 2025/26.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Dorset's own transparency report — A-roads broadly stable,
            B/C roads volatile, and a 2022 spike the council attributes to survey technology
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (368.1km — 9.7% of network): broadly stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2.2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 3.11% in 2022</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">22.5%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 17% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">75.3%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 81% in 2023</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              A-roads are surveyed annually using SCANNER laser-based technology. Condition is acceptable
              but not improving — green roads fell from 81% to 75.3% between 2023 and 2024.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (1,490.6km — 39% of network): the 2022 spike</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3.41%</td>
                    <td className="px-4 py-2 text-center">32.59%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.6%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13.31%</td>
                    <td className="px-4 py-2 text-center text-xs">B: 31.2% / C: 34.2%</td>
                    <td className="px-4 py-2 text-center text-xs">B: 60.38% / C: 48.9%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6.58%</td>
                    <td className="px-4 py-2 text-center">26.56%</td>
                    <td className="px-4 py-2 text-center">66.86%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5.93%</td>
                    <td className="px-4 py-2 text-center font-bold">28%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition B/C roads nearly quadrupled from the 2020 baseline (3.41% → 13.31%) before easing
              to 5.93%. Even after recovery, RED roads remain 74% above the 2020 level — and roughly a third
              of the B/C network is amber, meaning maintenance may be required soon.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">AMBER Spend Despite Rising Investment</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-amber-400">£25.0m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£29.8m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">90%</div>
                <div className="text-brand-300 text-sm">Planned preventative share 2025/26</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Dorset plans to exceed its DfT allocation by nearly £5m in 2025/26 and class 90% of spend as
              preventative — yet the DfT Spend scorecard is still{' '}
              <span className="text-amber-400">AMBER</span>. In 2020/21 the council underspent its allocation
              by £4.3m, and reactive maintenance rose to 18% in 2022/23 and 2023/24 — the same years pothole
              fills surged past 10,000.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,945km Unclassified Network
          </h2>
          <p className="text-surface-600 mb-8">
            51% of Dorset's roads are unclassified — assessed periodically, not annually
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.81%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11.79%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We assess our unclassified roads periodically to assess condition, supplemented by our annual inspection programme which ensures they meet appropriate safety standards."
              source="Dorset Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Periodic, Not Annual</h3>
              <p className="text-surface-600 text-sm mb-3">
                Classified A, B and C roads receive annual SCANNER surveys. Unclassified roads — over half
                the network — are assessed only "periodically." Dorset publishes RED percentages for U-roads
                but no amber or green breakdown, so the full condition picture for residential and village
                routes is incomplete.
              </p>
              <p className="text-surface-600 text-sm">
                At 12% RED in 2024, roughly <strong>233km of U-roads</strong> should be considered for
                maintenance — estate streets, village lanes and rural access roads where most local pothole
                damage occurs.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2022 U-Road Spike</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unclassified roads in RED condition doubled from 12.8% in 2021 to 25% in 2022 — the same year
                B/C roads hit their peak. The figure dropped to 11.79% in 2023, but the council does not
                explain the methodology behind U-road surveys with the same footnote given for B/C roads.
              </p>
              <p className="text-amber-800 text-sm">
                A swing from 12.8% to 25% and back within two years raises questions about survey consistency
                — and about what the council actually knew about U-road condition when your defect formed.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Dorset's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it during or after the 2022 spike?</li>
              <li>• If 12% of U-roads are RED at the last survey, what was done about yours specifically?</li>
              <li>• How does "periodic" assessment satisfy Section 41's duty on a 1,945km sub-network?</li>
              <li>• Does the annual safety inspection programme cover the defect location, or only carriageway-wide condition?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge for roads it only assesses periodically —
              while publishing incomplete condition data for the majority of its network.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            43,115 Carriageway Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many defects this network produces — and when the surge began
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway potholes</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Footway potholes</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Total</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,329</td>
                  <td className="px-6 py-4 text-center">392</td>
                  <td className="px-6 py-4 text-center font-bold">7,721</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7,026</td>
                  <td className="px-6 py-4 text-center">348</td>
                  <td className="px-6 py-4 text-center font-bold">7,374</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">7,643</td>
                  <td className="px-6 py-4 text-center">276</td>
                  <td className="px-6 py-4 text-center font-bold">7,919</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,625</td>
                  <td className="px-6 py-4 text-center">229</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,854</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10,492</td>
                  <td className="px-6 py-4 text-center">233</td>
                  <td className="px-6 py-4 text-center font-bold">10,725</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold">43,115</td>
                  <td className="px-6 py-4 text-center font-bold">1,478</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">44,593</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 Inflection Point</h3>
              <p className="text-surface-600 text-sm">
                Carriageway pothole fills jumped 39% from 7,643 in 2022/23 to 10,625 in 2023/24 — and stayed
                above 10,000 in 2024/25. That surge coincided with reactive maintenance rising to 18% of total
                spend. A network producing over 10,000 potholes a year is one where defects routinely form
                between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Preventative vs Reactive</h3>
              <p className="text-amber-800 text-sm">
                Dorset's own report states it aims to "increase the proportion of our budget spent on
                preventative maintenance." Planned preventative share rises to 90% in 2025/26 — but reactive
                maintenance still accounts for 13.5% in 2024/25 and produced over 10,000 pothole repairs.
                Preventative ambition does not eliminate reactive failure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The 2022 Methodology Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 2022 Condition Spike — In The Council's Own Words
          </h2>
          <p className="text-surface-600 mb-8">
            Dorset published the worst B/C condition figures in its five-year dataset — then attributed them to survey technology
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Data for B and C-class roads is collected annually. The 2022 figures reflect a more detailed breakdown due to the survey technology used (non-SCANNER)."
              source="Dorset Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="We aim to increase the proportion of our budget spent on preventative maintenance to extend the lifespan of our assets and reduce long-term costs in a planned and cost-effective manner."
              source="Dorset Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The Footnote Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Dorset switched to non-SCANNER survey technology for B/C roads in 2022, producing a more
                granular breakdown — and a RED figure of 13.31%, nearly quadruple the 2020 baseline. The council
                published the data and it remains on the public record regardless of methodology.
              </p>
              <p className="text-surface-600 text-sm">
                From 2026/2027, a new PAS 2161 standard will replace the current three-category system with
                five categories — another methodology change claimants should watch for when comparing year-on-year data.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road surveyed in 2022 when condition peaked?</li>
                <li>• If 13.31% of B/C roads were RED, what maintenance was scheduled for your route?</li>
                <li>• Did the non-SCANNER survey identify your defect — and if so, why wasn't it repaired?</li>
                <li>• How will the 2026 PAS 2161 change affect comparability of future condition data?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Resurfacing and Forward Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing Output vs Network Size
          </h2>
          <p className="text-surface-600 mb-8">
            How much of Dorset's 3,797km network actually gets structural treatment each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfaced / treated (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Structures maintained</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">98.4</td>
                  <td className="px-6 py-4 text-center">71</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">255.14</td>
                  <td className="px-6 py-4 text-center">62</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">159.77</td>
                  <td className="px-6 py-4 text-center">62</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">229.90</td>
                  <td className="px-6 py-4 text-center">42</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">212.70</td>
                  <td className="px-6 py-4 text-center font-bold">43</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">2025/26 Structural Maintenance Programme</h3>
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">~50km</div>
                <div className="text-brand-300 text-sm">Resurfacing and large structural patching (~£12m)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">~120km</div>
                <div className="text-brand-300 text-sm">Thin surface treatments (~£6.1m)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">~20km</div>
                <div className="text-brand-300 text-sm">Footway and cycleway treatment (~£750k)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">31</div>
                <div className="text-brand-300 text-sm">Highway structures projects (~£4.4m)</div>
              </div>
            </div>
            <p className="text-brand-200 text-sm">
              Even with ~170km of combined carriageway treatment planned, that covers under 5% of the
              3,797km network in a single year. The remaining 95% relies on inspection regimes and reactive
              pothole filling — the same reactive pipeline that processed 43,115 carriageway repairs in five years.
            </p>
          </div>

          <QuoteCard
            quote="increased investment in drainage climate change adaptation works and landslips, to reduce reactive costs and improve long-term network resilience"
            source="Dorset Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* What AMBER + GREEN best practice means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Best-Practice AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Dorset runs a documented asset management programme — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — HAMP aligned with Dorset Council Plan, documented asset management strategy
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Exceeds DfT capital allocation in four of the last five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 82–90% of spend classed as preventative across the period
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition broadly stable with annual SCANNER surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Innovative treatments — in-situ recycling, micro-asphalt, surface dressing
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing asset management plans. Generic claims will struggle on A-roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C roads hit 13.31% RED in 2022, still 74% above 2020 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 51% of network is U-roads assessed only "periodically" with incomplete RAG data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED spiked to 25% in 2022 — roughly 486km of residential routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 43,115 carriageway potholes filled in five years — defects outpace structural treatment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER spend — underspent DfT allocation by 21% in 2020/21, reactive share hit 18%
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN best-practice scorecards and a published HAMP, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the periodic assessment gap and incomplete RAG data are structural arguments</li>
              <li>• Timing relative to the 2022 condition spike and the 2023/24 pothole surge</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Dorset's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Dorset?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A documented council demands a documented claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road periodic-assessment argument</li>
                <li>• No prior-report search</li>
                <li>• No 2022 condition spike cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C 2022 RED spike documented</li>
                <li>✅ Periodic U-road assessment gap argued</li>
                <li>✅ 43,115 carriageway repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Dorset</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=dorset"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Dorset Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Dorset has GREEN best practice — does that block my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Best practice measures how the council manages its assets — ISO-aligned plans, documented
                strategy, innovative treatments. Section 58 turns on whether the specific defect that damaged
                your vehicle was reasonably inspected and repaired. Dorset is AMBER on condition, with B/C roads
                hitting 13.31% RED in 2022 and over 10,000 carriageway potholes filled in both 2023/24 and 2024/25.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What happened to Dorset road condition in 2022?
              </h3>
              <p className="text-surface-600 text-sm">
                B and C roads in RED condition jumped from 5.6% in 2021 to 13.31% in 2022 — nearly quadruple
                the 2020 baseline of 3.41%. Unclassified roads in RED hit 25%, up from 12.8% the year before.
                Dorset's own report notes the 2022 B/C figures "reflect a more detailed breakdown due to the
                survey technology used (non-SCANNER)" — but the council still published the numbers and they
                remain on the public record.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 1,944.7km — 51% of Dorset's 3,797km network. The council assesses
                them "periodically" rather than with the annual SCANNER surveys used on classified roads. At the
                last survey, 12% of U-roads were in RED condition — roughly 233km of residential streets, estate
                roads and village routes. Only RED percentages are published for U-roads; there is no amber/green breakdown.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Dorset plans 90% preventative spend in 2025/26 — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Preventative spend is a portfolio metric, not a defence for a specific pothole. Dorset's
                reactive share rose to 18% in 2022/23 and 2023/24 — the same years carriageway pothole fills
                jumped from 7,643 to over 10,600. The DfT Spend scorecard is AMBER, and in 2020/21 the council
                spent £16.15m against a £20.48m DfT allocation — 21% below the funded level.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs jumped in 2023/24 — does that mean Dorset's roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Dorset filled 10,625 carriageway potholes in 2023/24 and 10,492 in 2024/25 — a 39% jump
                from 2022/23 and still running at roughly 29 repairs every day. A network producing defects at
                that rate is one where potholes routinely form between inspections. Prior reports and photographic
                evidence of the specific defect remain decisive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.dorsetcouncil.gov.uk/w/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Dorset Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
